import React, { useState, useEffect } from "react"
import { getMaterialIconForLevel } from "./LuiBanner"

export interface ILuiToastMessageProps {
  /** "success" | "info" | "warning" | "error" */
  level: "success" | "info" | "warning" | "error"
  /** 0 means it will not disolve @default 4000 */
  displayTimeout?: number
  /** allows the consumer to control when Toast fades @default true */
  display?: boolean
  /** adds an X in the corner @default false */
  requireDismiss?: boolean
  /** called when all said and done, allows the consumer to remove from React Dom */
  onClose?: () => void
  children?: React.ReactNode
}

export const LuiToastMessage: React.FC<ILuiToastMessageProps> = ({
  level,
  displayTimeout = 4000, // copied from $toast-auto-transition-animation-time
  display = true,
  requireDismiss,
  onClose,
  children,
}: ILuiToastMessageProps) => {
  const [className, setClassname] = useState("lui-msg-toast-show")

  let materialIcon = getMaterialIconForLevel(level)

  function callOnCloseAfterDisolved() {
    // This is so the caller can remove it from the dom
    if (onClose) {
      setTimeout(onClose, displayTimeout === 0 ? 4000 : displayTimeout) // copied from $toast-auto-transition-animation-time
    }
  }

  useEffect(() => {
    if (!requireDismiss && displayTimeout !== 0) {
      setTimeout(() => {
        setClassname("lui-msg-toast-hide")
        callOnCloseAfterDisolved()
      }, displayTimeout)
    }
  }, [requireDismiss, displayTimeout, callOnCloseAfterDisolved])

  useEffect(() => {
    if (!display) {
      setClassname("lui-msg-toast-hide")
      callOnCloseAfterDisolved()
    }
  }, [display, callOnCloseAfterDisolved])

  return (
    <div
      role="status"
      data-testid={className}
      className={
        requireDismiss
          ? `lui-msg-${level} lui-msg-toast lui-msg-toast-dismissable ${className}`
          : `lui-msg-${level} lui-msg-toast ${className}`
      }
    >
      <i className="lui-msg-status-icon material-icons-round">{materialIcon}</i>
      {children}
      {requireDismiss && (
        <button
          aria-label="Close dialog"
          onClick={() => {
            setClassname("lui-msg-toast-hide")
            callOnCloseAfterDisolved()
          }}
        >
          <i className="material-icons-round">close</i>
        </button>
      )}
    </div>
  )
}
